import React, { useState, useContext, useCallback, useEffect } from "react"
import { UserContext } from "../../../context/userContext"
import _ from "lodash"
import styles from "../../../styles/Page-with-Sidebar.module.css"
import trcss from "../../../styles/Telerecovery.module.css"
import gmcss from "../../../styles/GlobalMessages.module.css"
import InvalidMessageLeftConvList from "./InvalidMessageLeftConvList"
import InvalidMessageChat from "./InvalidMessageChat"
import { trApiGetInvalidMessagesListByOrgId, trApiInvalidMessageSetRead } from "../telerecoveryApi"
import InvalidMessageReply from "./InvalidMessageReply"
import { LoaderMedium } from "../../../components/Loaders"
import { ReactComponent as BoxMessageIcon } from "../resources/box_messages.svg"
import NoData from "../NoData"
import { useQuery } from "@apollo/client"
import { GET_STAFF_NAMES } from "../Utils"

export default function InvalidMessagesTab() {
  const userCtx = useContext(UserContext)
  const { user } = userCtx.session
  const [invalidMessageConversations, setInvalidMessageConversations] = useState([])
  const [loadingList, setloadingList] = useState(true)
  const [selectedConvs, setSelectedConvs] = useState([])
  const [mounted, setMounted] = useState(false)
  const [showMessageReply, setShowMessageReply] = useState(false)
  const [staffNames, setStaffNames] = useState({})

  useEffect(() => {
    if (invalidMessageConversations.length > 0 && !mounted) {
      setMounted(true)
      selectConv(invalidMessageConversations[0])
    } else {
      if (selectedConvs.length > 0) {
        const _selectedConvs = invalidMessageConversations.filter((c) => {
          if (selectedConvs.some((s) => s.id === c.id)) {
            return c
          }
        })
        setSelectedConvs(_selectedConvs)
      }
    }
  }, [invalidMessageConversations])

  const { refetch: refetchStaffs } = useQuery(GET_STAFF_NAMES, {
    variables: {
      staff_id: user.staff_id,
      organization_id: user.organization_id,
      staff_ids: [user.staff_id]
    },
    skip: true,
  })

  const getStaffsNames = async (staffIds) => {
    if (staffIds.length > 0) {
      const res = await refetchStaffs({
        staff_id: user.staff_id,
        organization_id: user.organization_id,
        staff_ids: [...new Set(staffIds)]
      })
      if (res.data.getStaffNamesByIds?.data) {
        return JSON.parse(res.data.getStaffNamesByIds.data)
      }
    }
    return {}
  }

  const loadInvalidMessages = useCallback(() => {
    setloadingList(true)

    trApiGetInvalidMessagesListByOrgId(userCtx.tokenTelerecovery, user.organization_id).then(
      async (response) => {
        let convs = _.cloneDeep(response)

        const staffIds = [...new Set(
          convs
            .flatMap((c) =>
              c.messages
                .filter((m) => m.staffId !== null && m.staffId !== undefined)
                .map((m) => m.staffId)
            )
        )]
        const names = await getStaffsNames(staffIds.flat())

        setStaffNames(names)
        setInvalidMessageConversations(convs)
        setloadingList(false)
      },
      (err) => {
        console.log(err)
        setloadingList(false)
      }
    )
  }, [userCtx.tokenTelerecovery, user.organization_id])

  React.useEffect(() => {
    loadInvalidMessages()
  }, [loadInvalidMessages])

  const markConvRead = (conv) => {
    if (!conv.unread)
      return

    trApiInvalidMessageSetRead(userCtx.tokenTelerecovery, { convId: conv.id })
      .then((resp) => {
        const updatedList = invalidMessageConversations.map((c) => {
          if (c.from === conv.from) {
            return { ...c, unread: false }
          }
          return c
        })
        setInvalidMessageConversations(updatedList)
      },
        (err) => {
          console.log(err)
        })
  }

  const selectStandaloneConv = (conv) => {
    setShowMessageReply(false)
    setSelectedConvs([conv])
    markConvRead(conv)
  }

  const selectConv = (conv) => {
    setShowMessageReply(false)
    const selected = selectedConvs.some(c => c.id === conv.id)
    if (selected) {
      setSelectedConvs(selectedConvs.filter(c => c.id !== conv.id))
    } else {
      setSelectedConvs([...selectedConvs, conv])
      markConvRead(conv)
    }
  }

  const addReply = (reply) => {
    //Update the conversation list with the new reply
    const updatedList = invalidMessageConversations.map((c) => {
      if (selectedConvs.some((s) => s.id === c.id)) {
        return { ...c, messages: [...c.messages, reply] }
      }
      return c
    })
    setInvalidMessageConversations(updatedList)
    //Updated selected conversations with the new reply
    setSelectedConvs(selectedConvs.map((c) => {
      return { ...c, messages: [...c.messages, reply] }
    }))
  }

  if (loadingList) {
    return <LoaderMedium />
  }

  if (invalidMessageConversations.length === 0) {
    return (
      <div className={trcss.no_record_div} style={{ gap: "2px" }}>
        <NoData
          icon={
            <BoxMessageIcon
              style={{ padding: "8px", width: "100%", height: "100%" }}
            />
          }
        />
        <div style={{ width: "400px" }}>
          <p style={{ fontWeight: "600" }}>
            No messages
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={gmcss.invalidMessagesNote}>Invalid messages are SMS messages received by a telerecovery number in use at the organization, but from an incoming number that does not have valid permissions to communicate at the time it was sent. Supervisors or administrators may reply to these messages, which will be sent from the organization's telerecovery number.</div>
      <div className={styles.wrapper} style={{ minHeight: "600px", margin: "0px" }}>
        <InvalidMessageLeftConvList
          convList={invalidMessageConversations}
          selectConv={selectConv}
          selectStandaloneConv={selectStandaloneConv}
          selectedConvs={selectedConvs}
          loadingList={loadingList}
          showMessageReply={() => setShowMessageReply(true)}
          refreshConvs={loadInvalidMessages}
        />
        <div className={trcss.chatWrapper}>
          {selectedConvs.length > 0 && !showMessageReply &&
            <InvalidMessageChat
              selectedConvs={selectedConvs}
              showMessageReply={() => setShowMessageReply(true)}
              staffNames={staffNames}
            />
          }
          {selectedConvs.length > 0 && showMessageReply &&
            <InvalidMessageReply
              selectedConvs={selectedConvs}
              addReply={addReply}
              setSelectedConvs={setSelectedConvs}
              closeMessageReply={() => setShowMessageReply(false)}
            />
          }
        </div>
      </div>
    </>
  )
}